import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { footerData, navLinks } from '../data/mockData';
import SocialLinks from './footer/SocialLinks';
import ScrollToTop from './footer/ScrollToTop';
import FooterSubscribe from './footer/FooterSubscribe';

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  return (
  <>
    <div className="h-1 bg-[#00BCE4]"></div>

    <footer className="bg-[#e8e8e8] py-16 lg:py-20">
      <div className="section-container">
        <div className="grid md:grid-cols-3 gap-12 lg:gap-16">
          {/* Column 1: Logo & Address */}
          <div>
            <div className="mb-8">
              <img
                src="/imprint-logo-color.svg"
                alt="Imprint M.I.S."
                className="h-14 w-auto object-contain"
              />
            </div>

            <div
              className="text-sm text-gray-600 leading-relaxed space-y-1 mb-6"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              <p>{footerData.address.line1}</p>
              <p>{footerData.address.line2}</p>
              <p>{footerData.address.line3}</p>
              <p>{footerData.address.line4}</p>
            </div>

            <p className="text-xs text-gray-500 mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
              A member of...
            </p>

            <div className="flex gap-4 items-center">
              <a
                href="https://ipia.org.uk/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Visit the IPIA website"
                data-testid="footer-ipia-link"
                className="transition-opacity hover:opacity-80"
              >
                <img
                  src="/memberships/ipia.webp"
                  alt="Independent Print Industries Association (IPIA)"
                  className="h-16 w-16 rounded-full object-contain"
                  loading="lazy"
                />
              </a>
              <a
                href="https://picon.com/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Visit the Picon website"
                data-testid="footer-picon-link"
                className="transition-opacity hover:opacity-80"
              >
                <img
                  src="/memberships/picon.webp"
                  alt="Printing Industry Confederation (Picon)"
                  className="h-16 w-auto object-contain"
                  loading="lazy"
                />
              </a>
            </div>
          </div>

          {/* Column 2: Navigation */}
          <div>
            <h3 className="text-lg font-bold text-gray-600 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Navigation
            </h3>
            <nav className="space-y-3">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={(e) => {
                    e.preventDefault();
                    if (link.isPage) {
                      navigate(link.href);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                      return;
                    }
                    if (location.pathname !== '/') {
                      navigate('/' + link.href);
                      return;
                    }
                    const id = link.href.replace('#', '');
                    const el = document.getElementById(id);
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                  }}
                  data-testid={`footer-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className="block text-sm text-gray-600 hover:text-[#00BCE4] transition-colors duration-300"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {link.label}
                </a>
              ))}
            </nav>
          </div>

          {/* Column 3: Subscribe, Phone & Social */}
          <div>
            <FooterSubscribe />
            <h3 className="text-lg font-bold text-[#00BCE4] mt-8 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {footerData.phone}
            </h3>
            <SocialLinks links={footerData.socialLinks} />
            <a
              href="/privacy-policy"
              onClick={(e) => {
                e.preventDefault();
                navigate('/privacy-policy');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              data-testid="footer-privacy-link"
              className="text-sm text-gray-600 hover:text-[#00BCE4] transition-colors duration-300"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Privacy Policy
            </a>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-16 pt-8 border-t border-gray-300">
          <div className="text-center">
            <p className="text-xs text-gray-500" style={{ fontFamily: 'Inter, sans-serif' }}>
              {footerData.copyright}
            </p>
            <p className="text-xs text-gray-500 mt-1" style={{ fontFamily: 'Inter, sans-serif' }}>
              {footerData.regNumber}
            </p>
            <p className="text-xs text-gray-400 mt-3" style={{ fontFamily: 'Inter, sans-serif' }}>
              Designed &amp; Built by{' '}
              <a
                href="https://www.boostmarketing.agency"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-[#00BCE4] transition-colors duration-300 underline-offset-2 hover:underline"
                data-testid="footer-boost-credit"
              >
                Boost! Design &amp; Marketing Ltd
              </a>
              {' · '}
              <a
                href="https://www.boostmarketing.agency"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-[#00BCE4] transition-colors duration-300"
              >
                www.boostmarketing.agency
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>

    <ScrollToTop />
  </>
  );
};

export default Footer;
